import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getShipmentById, getShipmentPaymentBySN } from '../../store/reducers/Shipment/actions';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootStateType } from '../../store';
import PackagingDetails from './PackagingDetails';
import Title from '../../components/Title';
import ShipmentHistoryActions from './ShipmentHistoryActions';
import ShipperConsigneeDetails from './ShipperConsigneeDetails';
import { Alert, Grid } from '@mui/material';
import ShipmentCharges from './ShipmentCharges';
import ShipmentDetails from './ShipmentDetails';
import TrackingDetails from './TrackingDetails';
import RequiredDocuments from './RequiredDocuments';
import { resetShipmentById } from '@Reducers/Shipment';
import { getShipmentSettings } from '@Reducers/OrganizationSetup';
import Loader from '@Components/Loader';
import CancelAndTrackShipment from '@Components/CancelAndTrackShipment';
import { useWebsocket } from '@Hook/useWebsocket';
import API from '@Services/apiAxios';

const formatPickupDate = (date: any): string => {
  if (!date) return '';
  if (typeof date?.toDate === 'function') {
    return date.toDate().toUTCString();
  }
  const parsed = new Date(date);
  if (!isNaN(parsed.getTime())) {
    return parsed.toUTCString();
  }
  return String(date);
};

export function ViewSingleShipment() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id: shipmentId } = useParams();
  const { loading, shipmentCancelled } = useSelector((state: RootStateType) => state.shipmentHistory);
  const { shipmentById } = useSelector((state: RootStateType) => state.shipment);
  const { profile } = useSelector((state: RootStateType) => state.loginUser);
  const shipmentpayment = useSelector((state: RootStateType) => state.adminShipment.shipmentpayment);
  const { availableLimit } = useSelector((state: RootStateType) => state.Credit);

  const [clearanceDocs, setClearanceDocs] = useState<string[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);

  useEffect(() => {
    if (shipmentId) {
      dispatch(getShipmentSettings());
      dispatch(getShipmentById(shipmentId));
      dispatch(getShipmentPaymentBySN(shipmentId));
    }
    return () => {
      dispatch(resetShipmentById());
    };
  }, []);

  useEffect(() => {
    if (shipmentCancelled) {
      navigate('/shipment/history');
    }
  }, [shipmentCancelled]);

  useEffect(() => {
    if (!shipmentById?._consigneeId?.country) {
      setClearanceDocs([]);
      return;
    }

    let active = true;

    const fetchClearanceDocs = async () => {
      setLoadingDocs(true);
      try {
        const payload = {
          consignee: {
            country: shipmentById?._consigneeId?.country || '',
          },
          package: {
            unitOfMeasurement: shipmentById?._packageId?.unitOfMeasurement || 'kg-cm',
            packaging: !!shipmentById?._packageId?.packaging,
            insurance: !!shipmentById?._packageId?.insurance,
            isDocument: !!shipmentById?._packageId?.isDocument,
            isCod: !!shipmentById?._packageId?.isCod,
            isDropOff: !!shipmentById?._packageId?.isDropOff,
            pickupDate: formatPickupDate(shipmentById?._packageId?.pickupDate),
            timeSlot: shipmentById?._packageId?.timeSlot || '',
            codAmount: Number(shipmentById?._packageId?.codAmount) || 0,
            currency: shipmentById?._packageId?.currency || 'INR',
            box: (shipmentById?._packageId?.box || []).map((box: any, idx: number) => ({
              length: Number(box.length) || 0,
              width: Number(box.width) || 0,
              height: Number(box.height) || 0,
              weight: Number(box.weight) || 0,
              id: box.id || idx.toString(),
              products: (box.products || []).map((prod: any) => ({
                description: prod.description || '',
                quantity: Number(prod.quantity) || 0,
                unitValue: Number(prod.unitValue) || 0,
                HSNName: prod.HSNName || '',
                HSNCode: prod.HSNCode || '',
                _product: prod._product || null,
                _id: prod._id || prod.id || '',
                createdAt: prod.createdAt || new Date().toISOString(),
                updatedAt: prod.updatedAt || new Date().toISOString(),
              })),
            })),
            totalBoxWeight: 'Need calc',
            invoiceNumber: shipmentById?._packageId?.invoiceNumber || '',
            dutyCharge: {
              type:
                typeof shipmentById?.dutyCharge === 'object' && shipmentById?.dutyCharge?.type
                  ? shipmentById.dutyCharge.type
                  : typeof shipmentById?.dutyCharge === 'string'
                  ? shipmentById.dutyCharge
                  : 'DDP',
              charge:
                typeof shipmentById?.dutyCharge === 'object' && shipmentById?.dutyCharge?.charge !== undefined
                  ? Number(shipmentById.dutyCharge.charge)
                  : 0,
            },
          },
          purposeOfShipment:
            (shipmentById?.extraDetails as any)?.other?.purposeOfShipment ||
            (shipmentById?.extraDetails as any)?.purposeOfShipment ||
            'FOR_SALE',
        };

        const response = await API.post('/speedboxbot/clearance-documents', payload);

        if (active) {
          const docs = response?.data?.documents || [];
          const docNames = docs.map((doc: any) => doc.documentName).filter(Boolean);
          setClearanceDocs(docNames);
        }
      } catch (err) {
        console.error('Error fetching clearance documents:', err);
        if (active) {
          setClearanceDocs([]);
        }
      } finally {
        if (active) {
          setLoadingDocs(false);
        }
      }
    };

    fetchClearanceDocs();

    return () => {
      active = false;
    };
  }, [shipmentById?._id, shipmentById?.shipmentNumber]);

  useWebsocket({
    query: {
      channel: 'shipment-update-' + shipmentId,
    },
  });

  return (
    <Loader loading={loading} overly>
      <>
        <Title title="Shipment History" removeBorder />
        <ShipmentHistoryActions />
        <Grid sx={{ marginY: '20px' }} container justifyContent="space-between" alignItems="center">
          {/* Left Side Alert */}
          <Grid item>
            {profile?._accountId.isPrepaid
              ? Number(shipmentpayment?.payableAmount) > 0 &&
                shipmentById?.invoiceStatus === 'unpaid' && (
                  <Alert
                    variant="outlined"
                    severity="error"
                    className="w-fit border-grey sb-text-error sm-text"
                    sx={{ height: '44px', py: '2px' }}
                  >
                    This shipment has an outstanding payment. Please complete the payment to continue processing.
                  </Alert>
                )
              : availableLimit < 0 && (
                  <Alert
                    variant="outlined"
                    severity="error"
                    className="w-fit border-grey sb-text-error sm-text"
                    sx={{ height: '44px', py: '2px' }}
                  >
                    Your credit limit has been exceeded. Please clear the outstanding dues to proceed with this
                    shipment.
                  </Alert>
                )}
          </Grid>

          {/* Right Side Buttons */}
          <Grid item>
            {shipmentById?.status && (
              <CancelAndTrackShipment
                shipmentStatus={shipmentById.status}
                accountId={shipmentById?._userId._id}
                showAlert={
                  profile?._accountId.isPrepaid
                    ? Number(shipmentpayment?.payableAmount) > 0 && shipmentById?.invoiceStatus === 'unpaid'
                    : availableLimit < 0
                }
              />
            )}
          </Grid>
        </Grid>

        <TrackingDetails />
        <PackagingDetails />
        <Grid container columnSpacing={{ lg: 5, md: 7, sm: 0 }} rowSpacing={{ lg: 0, md: 0, sm: 7 }}>
          <Grid item lg={8} md={7} sm={12} xs={12}>
            <ShipperConsigneeDetails />
          </Grid>
          <Grid item lg={4} md={5} sm={12} xs={12}>
            <ShipmentCharges />
          </Grid>
        </Grid>
        <ShipmentDetails />
        {clearanceDocs.length > 0 && <RequiredDocuments clearanceDocs={clearanceDocs} loading={loadingDocs} />}
      </>
    </Loader>
  );
}

export default ViewSingleShipment;
